import {
    DEFAULT_WISHES_KEY,
    ICitiesResponse,
    initialMyWishesState,
    initialWishDetailsState,
    initialWishesState,
    IWish,
    IWishesResponse,
    IWishImage,
    IWishState,
    WishStatus
} from "./Helper";
import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {SliceNames} from "../constants";
import {
    createWish,
    deleteWish,
    duplicateWish,
    editWish,
    getCitiesWithCounts,
    getPastWinners,
    getProfileWishes,
    getWishDetails,
    getWishes,
    getWishImages,
    IGetWishesResponse,
    publishWish,
    redrawSelectedWinners,
    redrawWinners,
    terminateWish,
    uploadImages
} from "./ActionCreators";

const patchWishInCollection = (collection: IWishesResponse, wish: IWish): IWishesResponse => {
    const existsInCollection = collection.data.some((w) => w.id === wish.id)

    return {
        ...collection,
        data: [...collection.data]
            .filter((w) => {
                if (wish.id === w.id && wish.status === WishStatus.FINISHED) {
                    return false
                }
                return true
            })
            .map((w) => {
                if (wish.id === w.id) return {...wish, status: w.status}
                return w
            }),
        count: existsInCollection && wish.status === WishStatus.FINISHED
            ? Math.max(0, collection.count - 1)
            : collection.count
    }
}

const removeWishFromCollection = (collection: IWishesResponse, wishId: string): IWishesResponse => {
    const existsInCollection = collection.data.some((w) => w.id === wishId)
    return {
        ...collection,
        count: existsInCollection ? Math.max(0, collection.count - 1) : collection.count,
        data: [...collection.data].filter((w) => w.id !== wishId)
    }
}

const replaceWishInCollection = (collection: IWishesResponse, wish: IWish): IWishesResponse => ({
    ...collection,
    data: collection.data.map((w) => {
        if (w.id === wish.id) {
            return wish
        }
        return w
    })
})

const initialValue: IWishState = {
    items: initialWishesState,
    fetchWishesLoading: false,
    itemsByKey: {[DEFAULT_WISHES_KEY]: initialWishesState},
    fetchWishesLoadingByKey: {[DEFAULT_WISHES_KEY]: false},
    details: initialWishDetailsState,
    detailsLoading: false,
    imagesLoading: false,
    images: [],
    actionLoading: false,
    pastWinners: [],
    activeProfileWishes: initialMyWishesState,
    finishedProfileWishes: initialMyWishesState,
    profileWishesLoading: false,
    citiesWithCounts: [],
    citiesWithCountsLoading: false,
    lastWishUpdateChange: new Date().toISOString()
}

const wishSlice = createSlice({
    name: SliceNames.WISH,
    initialState: initialValue,
    reducers: {
        reloadWishAfterAction: (state, action: PayloadAction<IWish>) => {
            state.details = (action.payload.id === state.details.id) ?
                action.payload
                : state.details
            state.items = patchWishInCollection(state.items, action.payload)
            Object.keys(state.itemsByKey).forEach((key) => {
                state.itemsByKey[key] = patchWishInCollection(state.itemsByKey[key], action.payload)
            })
        },
        removeWish: (state, action: PayloadAction<string>) => {
            state.items = removeWishFromCollection(state.items, action.payload)
            Object.keys(state.itemsByKey).forEach((key) => {
                state.itemsByKey[key] = removeWishFromCollection(state.itemsByKey[key], action.payload)
            })
            state.activeProfileWishes = {
                ...state.activeProfileWishes,
                count: Math.max(0, state.activeProfileWishes.count - 1),
                data: [...state.activeProfileWishes.data].filter((w) => w.id !== action.payload)
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(
            getProfileWishes.pending, (state) => {
                state.profileWishesLoading = true
            })
            .addCase(
                getProfileWishes.fulfilled, (state, action: PayloadAction<any>) => {
                    state[`${action.payload.status as "active" | "finished"}ProfileWishes`] = action.payload.data
                    state.profileWishesLoading = false
                })
            .addCase(
                getProfileWishes.rejected, (state) => {
                    state.profileWishesLoading = false
                })
            .addCase(
                getWishes.pending, (state, action) => {
                    const key = action.meta.arg.key ?? DEFAULT_WISHES_KEY
                    state.fetchWishesLoadingByKey[key] = true
                    if (key === DEFAULT_WISHES_KEY) {
                        state.fetchWishesLoading = true
                    }
                })
            .addCase(
                getWishes.fulfilled, (state, action: PayloadAction<IGetWishesResponse>) => {
                    const {key, data} = action.payload
                    state.itemsByKey[key] = data
                    state.fetchWishesLoadingByKey[key] = false
                    if (key === DEFAULT_WISHES_KEY) {
                        state.items = data
                        state.fetchWishesLoading = false
                    }
                })
            .addCase(
                getWishes.rejected, (state, action) => {
                    const key = action.meta.arg.key ?? DEFAULT_WISHES_KEY
                    state.fetchWishesLoadingByKey[key] = false
                    if (key === DEFAULT_WISHES_KEY) {
                        state.fetchWishesLoading = false
                    }
                })
            .addCase(
                getPastWinners.fulfilled, (state, action: PayloadAction<IWish[]>) => {
                    state.pastWinners = action.payload
                })
            .addCase(
                getWishDetails.pending, (state) => {
                    state.detailsLoading = true
                })
            .addCase(
                getWishDetails.fulfilled, (state, action: PayloadAction<IWish>) => {
                    state.details = action.payload
                    state.detailsLoading = false
                })
            .addCase(
                getWishDetails.rejected, (state) => {
                    state.detailsLoading = false
                })
            .addCase(
                getWishImages.pending, (state) => {
                    state.imagesLoading = true
                })
            .addCase(
                getWishImages.fulfilled, (state, action: PayloadAction<IWishImage[]>) => {
                    state.images = action.payload
                    state.imagesLoading = false
                })
            .addCase(
                getWishImages.rejected, (state) => {
                    state.imagesLoading = false
                })
            .addCase(
                createWish.pending, (state) => {
                    state.actionLoading = true
                })
            .addCase(
                createWish.fulfilled, (state) => {
                    state.actionLoading = false
                    state.lastWishUpdateChange = new Date().toISOString()
                })
            .addCase(
                createWish.rejected, (state) => {
                    state.actionLoading = false
                })
            .addCase(
                deleteWish.pending, (state) => {
                    state.actionLoading = true
                })
            .addCase(
                deleteWish.fulfilled, (state) => {
                    state.lastWishUpdateChange = new Date().toISOString()
                    state.actionLoading = false
                })
            .addCase(
                deleteWish.rejected, (state) => {
                    state.actionLoading = false
                })
            .addCase(
                publishWish.pending, (state) => {
                    state.actionLoading = true
                })
            .addCase(
                publishWish.fulfilled, (state) => {
                    state.lastWishUpdateChange = new Date().toISOString()
                    state.actionLoading = false
                })
            .addCase(
                publishWish.rejected, (state) => {
                    state.actionLoading = false
                })
            .addCase(
                terminateWish.pending, (state) => {
                    state.actionLoading = true
                })
            .addCase(
                terminateWish.fulfilled, (state) => {
                    state.lastWishUpdateChange = new Date().toISOString()
                    state.actionLoading = false
                })
            .addCase(
                terminateWish.rejected, (state) => {
                    state.actionLoading = false
                })
            .addCase(
                editWish.pending, (state) => {
                    state.actionLoading = true
                })
            .addCase(
                editWish.fulfilled, (state, action: PayloadAction<IWish>) => {
                    state.items = replaceWishInCollection(state.items, action.payload)
                    Object.keys(state.itemsByKey).forEach((key) => {
                        state.itemsByKey[key] = replaceWishInCollection(state.itemsByKey[key], action.payload)
                    })
                    state.actionLoading = false
                })
            .addCase(
                editWish.rejected, (state) => {
                    state.actionLoading = false
                })
            .addCase(
                uploadImages.pending, (state) => {
                    state.actionLoading = true
                })
            .addCase(
                uploadImages.fulfilled, (state) => {

                    state.actionLoading = false
                })
            .addCase(
                uploadImages.rejected, (state) => {
                    state.actionLoading = false
                })
            .addCase(
                redrawWinners.pending, (state) => {
                    state.actionLoading = true
                })
            .addCase(
                redrawWinners.fulfilled, (state) => {
                    state.lastWishUpdateChange = new Date().toISOString()
                    state.actionLoading = false
                })
            .addCase(
                redrawWinners.rejected, (state) => {
                    state.actionLoading = false
                })
            .addCase(
                redrawSelectedWinners.pending, (state) => {
                    state.actionLoading = true
                })
            .addCase(
                redrawSelectedWinners.fulfilled, (state) => {
                    state.lastWishUpdateChange = new Date().toISOString()
                    state.actionLoading = false
                })
            .addCase(
                redrawSelectedWinners.rejected, (state) => {
                    state.actionLoading = false
                })
            .addCase(
                duplicateWish.pending, (state) => {
                    state.actionLoading = true
                })
            .addCase(
                duplicateWish.fulfilled, (state) => {
                    state.lastWishUpdateChange = new Date().toISOString()
                    state.actionLoading = false
                })
            .addCase(
                duplicateWish.rejected, (state) => {
                    state.actionLoading = false
                })
            .addCase(
                getCitiesWithCounts.pending, (state) => {
                    state.citiesWithCountsLoading = true
                })
            .addCase(
                getCitiesWithCounts.fulfilled, (state, action: PayloadAction<ICitiesResponse[]>) => {
                    state.citiesWithCounts = action.payload
                    state.citiesWithCountsLoading = false
                })
            .addCase(
                getCitiesWithCounts.rejected, (state) => {
                    state.citiesWithCountsLoading = false
                })
    }
})

export default wishSlice.reducer

export const {reloadWishAfterAction, removeWish} = wishSlice.actions
